// ─────────────────────────────────────────────────────────────────────────────
// Glass + branded-ambient design tokens
//
// A small, reusable set of style objects that add subtle glassmorphism and soft
// brand-color "light patches" to selected premium surfaces. The app stays
// predominantly white and highly readable — glass is used as an accent, never
// everywhere. Do NOT apply these to tables, forms, or dialogs.
//
// Spec: 75–90% surface opacity · 12–24px blur · thin white borders ·
//       very subtle shadows · 16–24px radius · smooth transitions.
// ─────────────────────────────────────────────────────────────────────────────

export const BRAND = '#1e3a52';

// Large, soft brand-color light patches over a near-white base. Applied to the
// main app background so every page gets a calm, ambient sense of depth — the
// glass surfaces above then have something to refract. Use with
// `backgroundAttachment: 'fixed'` so the light feels stable while content scrolls.
export const ambientBackground = `
    radial-gradient(1200px 820px at 10% -12%, rgba(30,58,82,0.07) 0%, rgba(30,58,82,0) 55%),
    radial-gradient(1000px 720px at 102% 0%, rgba(59,94,127,0.055) 0%, rgba(59,94,127,0) 52%),
    radial-gradient(1000px 900px at 88% 116%, rgba(30,58,82,0.05) 0%, rgba(30,58,82,0) 55%),
    linear-gradient(180deg, #f6f9fb 0%, #f8fafc 100%)
`;

// Subtle frosted-glass surface — the default for cards/panels sitting over the
// ambient background. ~78% white so text stays crisp.
export const glassSurface = {
    backgroundColor: 'rgba(255,255,255,0.78)',
    backdropFilter: 'blur(16px) saturate(140%)',
    WebkitBackdropFilter: 'blur(16px) saturate(140%)',
    border: '1px solid rgba(255,255,255,0.66)',
    borderRadius: '18px',
    boxShadow: '0 1px 2px rgba(16,42,66,0.04), 0 12px 32px -14px rgba(30,58,82,0.16)',
    transition: 'box-shadow 0.25s ease, transform 0.25s ease, background-color 0.25s ease',
};

// A touch more opaque (~88%) for content-dense surfaces where contrast matters.
export const glassSurfaceStrong = {
    backgroundColor: 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    border: '1px solid rgba(255,255,255,0.7)',
    borderRadius: '18px',
    boxShadow: '0 1px 2px rgba(16,42,66,0.05), 0 16px 40px -16px rgba(30,58,82,0.18)',
    transition: 'box-shadow 0.25s ease, transform 0.25s ease',
};

// Gentle lift for clickable glass cards.
export const glassHover = {
    cursor: 'pointer',
    '&:hover': {
        boxShadow: '0 2px 6px rgba(16,42,66,0.06), 0 22px 46px -14px rgba(30,58,82,0.26)',
        transform: 'translateY(-2px)',
    },
};

// Frosted white with a faint brand glow in one corner — for AI / accent surfaces
// (assistant panels, insight cards) so they read as "special" without shouting.
export const brandTintGlass = {
    ...glassSurfaceStrong,
    backgroundColor: 'rgba(255,255,255,0.8)',
    backgroundImage:
        'radial-gradient(135% 130% at 100% 0%, rgba(30,58,82,0.10) 0%, rgba(30,58,82,0) 52%),' +
        'radial-gradient(120% 120% at 0% 100%, rgba(59,94,127,0.07) 0%, rgba(59,94,127,0) 55%)',
};

// Frosted glass tuned for the DARK navy sidebar (light-on-dark). Used for the
// profile strip so it reads as a distinct, premium panel.
export const glassDark = {
    backgroundColor: 'rgba(255,255,255,0.07)',
    backdropFilter: 'blur(14px) saturate(140%)',
    WebkitBackdropFilter: 'blur(14px) saturate(140%)',
    border: '1px solid rgba(255,255,255,0.13)',
    borderRadius: '16px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 10px 24px -14px rgba(0,0,0,0.55)',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
};
